/**
 * Canonical enums — mirrored EXACTLY (names + values + order) from
 * docs/plans/01-data-model-and-rls.md §1 (LOCKED) and guaranteed by
 * docs/plans/00-foundation-architecture.md §15.1.
 *
 * Each enum is exported BOTH as a readonly value array (for runtime checks,
 * zod, and the enum-mirror test) and as a derived union type. The value
 * arrays are the single source of truth on the TS side; @gym/db-types mirrors
 * the same Postgres `create type ... as enum (...)` definitions.
 *
 * DO NOT reorder or rename — downstream plans and the DB reference these
 * literally.
 */

export const role = ['owner', 'manager', 'front_desk', 'trainer', 'member'] as const;
export type Role = (typeof role)[number];

export const plan_type = [
  'monthly',
  'quarterly',
  'annual',
  'unlimited',
  'off_peak',
  'session_pack',
  'day_pass',
  'punch_card',
  'class_pack',
  'pt_pack',
] as const;
export type PlanType = (typeof plan_type)[number];

export const membership_status = ['pending', 'active', 'expired', 'frozen', 'cancelled'] as const;
export type MembershipStatus = (typeof membership_status)[number];

export const payment_method = [
  'cash',
  'card',
  'bank_transfer',
  'voucher',
  'gift_card',
  'online',
] as const;
export type PaymentMethod = (typeof payment_method)[number];

export const payment_status = [
  'pending',
  'completed',
  'failed',
  'refunded',
  'voided',
  'partially_refunded',
] as const;
export type PaymentStatus = (typeof payment_status)[number];

export const payment_kind = [
  'membership',
  'retail',
  'pt_session',
  'class_pack',
  'day_pass',
  'other',
  'refund',
] as const;
export type PaymentKind = (typeof payment_kind)[number];

export const fiscal_status = [
  'pending',
  'issued',
  'failed_retriable',
  'failed',
  'voided',
  'not_required',
] as const;
export type FiscalStatusEnum = (typeof fiscal_status)[number];

export const fiscal_doc_type = ['receipt', 'void', 'credit_note', 'z_report', 'e_invoice'] as const;
export type FiscalDocType = (typeof fiscal_doc_type)[number];

export const checkin_source = ['app_qr', 'card', 'manual', 'turnstile'] as const;
export type CheckinSource = (typeof checkin_source)[number];

export const checkin_result = [
  'granted',
  'denied_expired',
  'denied_unpaid',
  'denied_inactive',
  'denied_pack_empty',
  'granted_override',
  // Added by 0032_billing_state (AC-02): overdue member allowed with a warning.
  'allowed_warn_overdue',
] as const;
export type CheckinResult = (typeof checkin_result)[number];

export const booking_status = [
  'booked',
  'attended',
  'cancelled',
  'no_show',
  'late_cancel',
] as const;
export type BookingStatus = (typeof booking_status)[number];

export const waitlist_status = ['waiting', 'promoted', 'expired', 'cancelled'] as const;
export type WaitlistStatus = (typeof waitlist_status)[number];

export const class_session_status = ['scheduled', 'cancelled', 'completed'] as const;
export type ClassSessionStatus = (typeof class_session_status)[number];

export const stock_movement_type = [
  'purchase_in',
  'sale_out',
  'adjustment',
  'wastage',
  'transfer',
  'return_in',
  'return_out',
] as const;
export type StockMovementType = (typeof stock_movement_type)[number];

export const purchase_order_status = [
  'draft',
  'ordered',
  'partially_received',
  'received',
  'cancelled',
] as const;
export type PurchaseOrderStatus = (typeof purchase_order_status)[number];

export const expense_category = [
  'rent',
  'salary',
  'utilities',
  'equipment',
  'marketing',
  'supplies',
  'tax',
  'other',
] as const;
export type ExpenseCategory = (typeof expense_category)[number];

export const discount_type = [
  'percentage',
  'fixed_amount',
  'promo_code',
  'voucher',
  'gift_card',
] as const;
export type DiscountType = (typeof discount_type)[number];

export const notification_channel = ['push', 'sms', 'email'] as const;
export type NotificationChannel = (typeof notification_channel)[number];

export const notification_status = ['queued', 'sent', 'delivered', 'failed', 'suppressed'] as const;
export type NotificationStatus = (typeof notification_status)[number];

export const campaign_status = ['draft', 'scheduled', 'running', 'completed', 'cancelled'] as const;
export type CampaignStatus = (typeof campaign_status)[number];

export const consent_type = [
  'terms',
  'privacy',
  'marketing_push',
  'marketing_sms',
  'marketing_email',
  'social_leaderboard',
  'health_data',
] as const;
export type ConsentType = (typeof consent_type)[number];

export const freeze_status = ['requested', 'approved', 'active', 'rejected', 'ended'] as const;
export type FreezeStatus = (typeof freeze_status)[number];

export const commission_basis = [
  'membership_sold',
  'pt_session',
  'retail_sale',
  'class_taught',
] as const;
export type CommissionBasis = (typeof commission_basis)[number];

export const gender = ['male', 'female', 'other', 'unspecified'] as const;
export type Gender = (typeof gender)[number];

export const locale_code = ['mk', 'sq', 'en'] as const;
export type LocaleCode = (typeof locale_code)[number];

export const tax_rate_code = ['A', 'B', 'C', 'EXEMPT'] as const;
export type TaxRateCode = (typeof tax_rate_code)[number];

/**
 * Registry of every enum keyed by its canonical (DB) name — used by the
 * enum-mirror test and any generic enum tooling.
 */
export const enums = {
  role,
  plan_type,
  membership_status,
  payment_method,
  payment_status,
  payment_kind,
  fiscal_status,
  fiscal_doc_type,
  checkin_source,
  checkin_result,
  booking_status,
  waitlist_status,
  class_session_status,
  stock_movement_type,
  purchase_order_status,
  expense_category,
  discount_type,
  notification_channel,
  notification_status,
  campaign_status,
  consent_type,
  freeze_status,
  commission_basis,
  gender,
  locale_code,
  tax_rate_code,
} as const;

export type EnumName = keyof typeof enums;
